import { JSONHistory } from '../utils/JSONHistory.js'

/**
 * JSONHistoryModal - Modal component for browsing JSON history
 */
export class JSONHistoryModal {
  constructor(options = {}) {
    this.options = {
      onSelect: null, // Callback when a history item is selected
      onDelete: null, // Callback when a history item is deleted
      ...options
    }

    this.jsonHistory = new JSONHistory()
    this.modal = null
    this.isVisible = false
    this.currentSearch = ''
    this.currentEntries = []
  }

  /**
   * Show the history modal
   */
  show() {
    if (this.isVisible) return

    this.createModal()
    document.body.appendChild(this.modal)
    this.isVisible = true

    // Focus management
    this.modal.focus()

    // Load and display history
    this.refreshHistory()

    // Setup event listeners
    this.setupEventListeners()
  }

  /**
   * Hide the history modal
   */
  hide() {
    if (!this.isVisible || !this.modal) return

    this.modal.remove()
    this.modal = null
    this.isVisible = false
  }

  /**
   * Create the modal DOM structure
   */
  createModal() {
    this.modal = document.createElement('div')
    this.modal.className = 'json-history-modal-overlay'
    this.modal.setAttribute('tabindex', '-1')
    this.modal.setAttribute('role', 'dialog')
    this.modal.setAttribute('aria-modal', 'true')
    this.modal.setAttribute('aria-labelledby', 'history-modal-title')

    this.modal.innerHTML = `
            <div class="json-history-modal">
                <div class="json-history-header">
                    <h3 id="history-modal-title">📚 JSON History</h3>
                    <div class="json-history-actions">
                        <button type="button" class="history-action-btn clear-all-btn" title="Clear all history">
                            🗑️ Clear All
                        </button>
                        <button type="button" class="json-history-close" aria-label="Close history">✕</button>
                    </div>
                </div>
                
                <div class="json-history-search">
                    <input type="text" class="history-search-input" placeholder="Search JSON history..." />
                    <div class="history-stats">
                        <span class="history-count">0 entries</span>
                    </div>
                </div>

                <div class="json-history-content">
                    <div class="history-list">
                        <!-- History entries will be populated here -->
                    </div>
                    
                    <div class="history-empty" style="display: none;">
                        <div class="empty-state">
                            <div class="empty-icon">📝</div>
                            <h4>No JSON History Yet</h4>
                            <p>Start pasting JSON content to build your history.</p>
                        </div>
                    </div>
                </div>
                
                <div class="json-history-footer">
                    <div class="history-info">
                        <span class="storage-info">Stored locally in your browser</span>
                    </div>
                    <div class="history-footer-actions">
                        <button type="button" class="history-action-btn export-btn">📤 Export</button>
                        <button type="button" class="history-action-btn import-btn">📥 Import</button>
                    </div>
                </div>
            </div>
        `
  }

  /**
   * Setup event listeners for the modal
   */
  setupEventListeners() {
    if (!this.modal) return

    const closeBtn = this.modal.querySelector('.json-history-close')
    const searchInput = this.modal.querySelector('.history-search-input')
    const clearAllBtn = this.modal.querySelector('.clear-all-btn')
    const exportBtn = this.modal.querySelector('.export-btn')
    const importBtn = this.modal.querySelector('.import-btn')

    // Close button
    closeBtn.addEventListener('click', () => this.hide())

    // Search functionality
    searchInput.addEventListener('input', (e) => {
      this.currentSearch = e.target.value
      this.refreshHistory()
    })

    // Clear all history
    clearAllBtn.addEventListener('click', () => this.handleClearAll())

    // Export/Import
    exportBtn.addEventListener('click', () => this.handleExport())
    importBtn.addEventListener('click', () => this.handleImport())

    // Click outside to close
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.hide()
      }
    })

    // Escape key to close
    this.modal.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hide()
      }
    })
  }

  /**
   * Refresh the history display
   */
  refreshHistory() {
    const entries = this.currentSearch
      ? this.jsonHistory.searchHistory(this.currentSearch)
      : this.jsonHistory.getHistory()

    this.currentEntries = entries
    this.renderHistoryList(entries)
    this.updateStats()
  }

  /**
   * Render the history list
   * @param {Array} entries - History entries to render
   */
  renderHistoryList(entries) {
    const listContainer = this.modal.querySelector('.history-list')
    const emptyState = this.modal.querySelector('.history-empty')

    if (entries.length === 0) {
      listContainer.innerHTML = ''
      emptyState.style.display = 'block'
      return
    }

    emptyState.style.display = 'none'

    listContainer.innerHTML = entries.map(entry => this.renderHistoryEntry(entry)).join('')

    // Add event listeners to entry actions
    this.setupEntryEventListeners()
  }

  /**
   * Render a single history entry
   * @param {Object} entry - History entry
   * @returns {string} HTML string for the entry
   */
  renderHistoryEntry(entry) {
    const date = new Date(entry.timestamp).toLocaleString()
    const sizeKB = (entry.size / 1024).toFixed(1)
    const validIcon = entry.isValid ? '✅' : '❌'
    const truncatedContent = entry.content.length > 200
      ? entry.content.substring(0, 200) + '...'
      : entry.content

    return `
            <div class="history-entry" data-id="${entry.id}">
                <div class="history-entry-header">
                    <div class="history-entry-info">
                        <span class="history-entry-preview">${entry.preview}</span>
                        <div class="history-entry-meta">
                            <span class="history-timestamp">${date}</span>
                            <span class="history-size">${sizeKB} KB</span>
                            <span class="history-valid" title="${entry.isValid ? 'Valid JSON' : 'Invalid JSON'}">${validIcon}</span>
                        </div>
                    </div>
                    <div class="history-entry-actions">
                        <button type="button" class="history-entry-btn select-btn" data-id="${entry.id}" title="Load this JSON">
                            📋 Load
                        </button>
                        <button type="button" class="history-entry-btn delete-btn" data-id="${entry.id}" title="Delete this entry">
                            🗑️
                        </button>
                    </div>
                </div>
                <div class="history-entry-content">
                    <pre class="history-json-preview">${this.escapeHtml(truncatedContent)}</pre>
                </div>
            </div>
        `
  }

  /**
   * Setup event listeners for history entries
   */
  setupEntryEventListeners() {
    const selectButtons = this.modal.querySelectorAll('.select-btn')
    const deleteButtons = this.modal.querySelectorAll('.delete-btn')

    selectButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = e.target.dataset.id
        this.handleSelectEntry(id)
      })
    })

    deleteButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = e.target.dataset.id
        this.handleDeleteEntry(id)
      })
    })
  }

  /**
   * Handle selecting a history entry
   * @param {string} id - Entry ID
   */
  handleSelectEntry(id) {
    const entry = this.jsonHistory.getEntry(id)
    if (entry && this.options.onSelect) {
      this.options.onSelect(entry)
      this.hide()
    }
  }

  /**
   * Handle deleting a history entry
   * @param {string} id - Entry ID
   */
  handleDeleteEntry(id) {
    if (confirm('Are you sure you want to delete this JSON from history?')) {
      const success = this.jsonHistory.deleteEntry(id)
      if (success) {
        this.refreshHistory()
        if (this.options.onDelete) {
          this.options.onDelete(id)
        }
      }
    }
  }

  /**
   * Handle clearing all history
   */
  handleClearAll() {
    if (confirm('Are you sure you want to clear all JSON history? This cannot be undone.')) {
      const success = this.jsonHistory.clearHistory()
      if (success) {
        this.refreshHistory()
        if (this.options.onDelete) {
          this.options.onDelete('all')
        }
      }
    }
  }

  /**
   * Handle exporting history
   */
  handleExport() {
    try {
      const historyJson = this.jsonHistory.exportHistory()
      const blob = new Blob([historyJson], { type: 'application/json' })
      const url = URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = url
      a.download = `json-history-${new Date().toISOString().split('T')[0]}.json`
      a.click()

      URL.revokeObjectURL(url)
    } catch (error) {
      alert('Failed to export history: ' + error.message)
    }
  }

  /**
   * Handle importing history
   */
  handleImport() {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'

    input.addEventListener('change', (e) => {
      const file = e.target.files[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const success = this.jsonHistory.importHistory(e.target.result)
          if (success) {
            this.refreshHistory()
            alert('History imported successfully!')
          } else {
            alert('Failed to import history. Please check the file format.')
          }
        } catch (error) {
          alert('Failed to import history: ' + error.message)
        }
      }
      reader.readAsText(file)
    })

    input.click()
  }

  /**
   * Update statistics display
   */
  updateStats() {
    const stats = this.jsonHistory.getStats()
    const countElement = this.modal.querySelector('.history-count')

    if (countElement) {
      const displayCount = this.currentSearch ? this.currentEntries.length : stats.totalEntries
      countElement.textContent = `${displayCount} ${displayCount === 1 ? 'entry' : 'entries'}`
    }
  }

  /**
   * Escape HTML for safe display
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  /**
   * Toggle the modal visibility
   */
  toggle() {
    if (this.isVisible) {
      this.hide()
    } else {
      this.show()
    }
  }
}
